import { toast } from 'sonner';
import { startTransition } from 'react';
import { useMutation } from '@apollo/client/react';

import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AppMembershipRoleEnum } from 'src/graphql/schema';
import { AppMembershipUpdateDocument } from 'src/graphql/actions/appMemberships/appMembershipUpdate.mutation';
import { cn } from 'src/lib/utils';

import { roleOptions, roleOptionsDict } from '../model/roleOptions';
import { refetchAppMemberships } from '../api';

const RoleCell = ({ role, id, canUpdate = false }: { role: string; id: string; canUpdate?: boolean }) => {
  const [updateAppMembership, { loading }] = useMutation(AppMembershipUpdateDocument);

  const onUpdate = async (role: AppMembershipRoleEnum) => {
    try {
      await updateAppMembership({
        variables: {
          input: {
            appMembershipId: id,
            appMembershipParams: {
              role,
            },
          },
        },
      });
      toast.success('Member access has been updated');
      startTransition(() => refetchAppMemberships());
    } catch {
      toast.error('Failed to update member access');
    }
  };

  if (canUpdate) {
    return (
      <Select value={role} onValueChange={onUpdate}>
        <SelectTrigger
          variant="ghost"
          justify="end"
          disabled={loading}
          className="border-solid border border-transparent hover:border-border"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {roleOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Badge
      variant="pill"
      className={cn('font-semibold border-0 py-1', {
        'bg-sky-100 text-sky-700': role === AppMembershipRoleEnum.Manager,
        'bg-muted text-slate-700': role === AppMembershipRoleEnum.Viewer,
      })}
    >
      {roleOptionsDict[role as AppMembershipRoleEnum]}
    </Badge>
  );
};

export default RoleCell;
